// Lógica PURA do detector de FPS do health-watcher (long-session-cleaner.ts).
//
// Extraído pra testar isoladamente (sem rAF/DOM/QoE) — mesma estratégia do
// endpoint-failover.ts / play-dedup.ts. Cobre o invariante crítico da entrega:
// um travamento CURTO (ex.: 15s a 8fps numa janela de 30s) PRECISA disparar o
// `fps_drop`, em vez de diluir na média da janela inteira.
//
// PROBLEMA QUE RESOLVE (débito P1 da spec performance.md §9.5):
// medir o FPS e emitir o `health_sample` tinham a MESMA cadência (30s). O FPS
// reportado era frames*1000/elapsed numa janela de 30s — uma média. Um drop
// curto (15-20s a 8fps + resto a 60fps ≈ média 34fps, acima do threshold 24)
// sumia da telemetria: o detector nunca via FPS abaixo do threshold. Pior
// justamente no tier baixo (Tizen 2017), onde os micro-travamentos importam.
//
// DESACOPLAMENTO medição × emissão:
// - MEDIÇÃO: buckets curtos (~5s, como o fps-watchdog original media). Cada
//   bucket fecha com seu próprio FPS.
// - EMISSÃO: agrega a janela de 30s (6 buckets) num único `health_sample`,
//   preservando o `minFps` (pior bucket) além do `avgFps`. Mantém o objetivo
//   do Fernando (emite a cada 30s, -40% de volume) E recupera a sensibilidade.
// - DETECÇÃO: o `fps_drop` passa a olhar o `minFps` da janela, não a média.
//   Um único bucket de 5s abaixo do threshold já marca a janela como "ruim".

/** Default da janela de medição curta (bucket). ~5s, como o watchdog original. */
export const FPS_BUCKET_MS = 5_000
/** Default da janela de emissão (agrega buckets num health_sample). */
export const FPS_EMIT_MS = 30_000
/** FPS abaixo disso (no PIOR bucket da janela) = problema. */
export const FPS_DROP_THRESHOLD = 24
/** Janelas de emissão consecutivas com minFps baixo pra emitir `fps_drop`. */
export const FPS_DROP_SUSTAINED_WINDOWS = 2

/** Resultado fechado de uma janela de emissão (30s), agregando seus buckets. */
export interface FpsWindowResult {
  /** FPS médio da janela inteira (frames totais / tempo total). Reportado como
   *  `fps` no health_sample — preserva o baseline histórico do Grafana. */
  avgFps: number
  /** Pior bucket da janela (menor FPS entre os sub-intervalos de ~5s).
   *  É o número que o detector usa: drop curto não dilui. */
  minFps: number
  /** Quantos buckets fecharam dentro da janela (diagnóstico/observabilidade). */
  bucketCount: number
}

/**
 * Acumulador de frames com medição em buckets curtos e emissão agregada.
 *
 * Uso (no rAF tick): chame `frame(now)` a cada `requestAnimationFrame`. Ele
 * fecha buckets internos sozinho ao cruzar `bucketMs`. Quando `frame` retorna
 * um `FpsWindowResult` (não-null), a janela de emissão (`emitMs`) fechou e o
 * caller deve emitir o `health_sample` com `avgFps`/`minFps` e rodar o detector.
 *
 * Toda a aritmética de tempo é injetada (`now` em ms) — sem `Date.now()` nem
 * rAF aqui dentro, o que torna o acumulador 100% determinístico e testável.
 */
export class FpsWindowAccumulator {
  private readonly bucketMs: number
  private readonly emitMs: number

  /** Início da janela de emissão atual (ms). -1 = não inicializado. */
  private windowStart = -1
  /** Início do bucket curto atual (ms). */
  private bucketStart = -1
  /** Frames contados no bucket curto atual. */
  private bucketFrames = 0
  /** FPS de cada bucket já fechado dentro da janela de emissão atual. */
  private bucketFps: number[] = []
  /** Frames totais da janela de emissão atual (pro avgFps). */
  private windowFrames = 0

  constructor(opts?: { bucketMs?: number; emitMs?: number }) {
    this.bucketMs = opts?.bucketMs ?? FPS_BUCKET_MS
    this.emitMs = opts?.emitMs ?? FPS_EMIT_MS
  }

  /** (Re)inicia a contagem ancorada em `now`. Use no boot e após resume de
   *  visibility (a primeira sample pós-standby teria elapsed=horas → FPS≈0). */
  reset(now: number): void {
    this.windowStart = now
    this.bucketStart = now
    this.bucketFrames = 0
    this.bucketFps = []
    this.windowFrames = 0
  }

  /**
   * Registra um frame em `now` (ms). Fecha buckets e a janela de emissão
   * conforme o tempo cruza os limites.
   *
   * @returns o resultado agregado quando a janela de emissão fechou; senão null.
   */
  frame(now: number): FpsWindowResult | null {
    if (this.windowStart < 0) this.reset(now)

    this.bucketFrames++
    this.windowFrames++

    // Fecha buckets curtos que já cobriram bucketMs. Loop (não if) pra cobrir
    // o caso de um frame chegar muito atrasado e pular mais de um bucket — sem
    // ele, um gap longo inflaria o FPS do bucket seguinte.
    while (now - this.bucketStart >= this.bucketMs) {
      const elapsed = now - this.bucketStart
      // Atribui os frames acumulados ao bucket que está fechando. Buckets
      // "vazios" extras de um gap longo entram como 0fps (travou de fato).
      const fps = Math.round((this.bucketFrames * 1000) / elapsed)
      this.bucketFps.push(fps)
      this.bucketFrames = 0
      this.bucketStart += this.bucketMs
    }

    // Janela de emissão fechou?
    if (now - this.windowStart >= this.emitMs) {
      return this.closeWindow(now)
    }
    return null
  }

  /** Fecha a janela de emissão: agrega buckets, calcula avg/min e reinicia. */
  private closeWindow(now: number): FpsWindowResult {
    // Bucket parcial em aberto no fechamento da janela: conta também (senão os
    // frames do "rabo" da janela somem do avg e do min). Só vale se houve
    // algum tempo decorrido pra evitar divisão por ~0.
    const partialElapsed = now - this.bucketStart
    if (this.bucketFrames > 0 && partialElapsed > 0) {
      const fps = Math.round((this.bucketFrames * 1000) / partialElapsed)
      this.bucketFps.push(fps)
    }

    const windowElapsed = now - this.windowStart
    const avgFps =
      windowElapsed > 0 ? Math.round((this.windowFrames * 1000) / windowElapsed) : 0
    // minFps = pior bucket. Sem buckets fechados (janela curtíssima, edge), cai
    // pro avg pra nunca reportar 0 espúrio.
    const minFps = this.bucketFps.length > 0 ? Math.min(...this.bucketFps) : avgFps

    const result: FpsWindowResult = {
      avgFps,
      minFps,
      bucketCount: this.bucketFps.length,
    }

    // Reancora a próxima janela em `now`.
    this.reset(now)
    return result
  }
}

/** Estado do detector de `fps_drop` entre janelas de emissão. */
export interface FpsDropState {
  /** Janelas consecutivas com minFps abaixo do threshold. */
  lowStreak: number
}

/** Decisão do detector ao fechar uma janela de emissão. */
export type FpsDropDecision =
  /** Emitir o evento `fps_drop` (borda: bateu o sustained agora). */
  | { type: 'emit'; minFps: number; lowStreak: number }
  /** Não emitir (FPS normal, ou já emitiu nesta sequência de drop). */
  | { type: 'silent'; lowStreak: number }

interface DecideFpsDropInput {
  /** Pior bucket da janela que acabou de fechar. */
  minFps: number
  /** Estado anterior do detector (streak acumulado). */
  state: FpsDropState
  /** Threshold de FPS (default FPS_DROP_THRESHOLD). */
  threshold?: number
  /** Janelas consecutivas baixas pra disparar (default FPS_DROP_SUSTAINED_WINDOWS). */
  sustainedWindows?: number
}

/**
 * Decide, ao fechar uma janela de emissão, se deve emitir o `fps_drop`.
 *
 * REGRA (edge-only, igual ao comportamento original — evita spam em devices com
 * FPS cronicamente baixo, ex.: Tizen 2017 decodificando 4K):
 * - `minFps` abaixo do threshold incrementa o streak.
 * - Emite SÓ na borda em que o streak ATINGE `sustainedWindows` (não a cada
 *   janela baixa subsequente). O `health_sample` contínuo já carrega o FPS pro
 *   baseline; `fps_drop` é sinal de transição alto→baixo.
 * - `minFps` normal zera o streak.
 *
 * A diferença pro código antigo é só a ENTRADA: antes era a média da janela de
 * 30s (diluía drops curtos); agora é o `minFps` (pior bucket de ~5s), então um
 * micro-travamento de um único bucket já marca a janela como ruim.
 */
export function decideFpsDrop(input: DecideFpsDropInput): FpsDropDecision {
  const threshold = input.threshold ?? FPS_DROP_THRESHOLD
  const sustained = input.sustainedWindows ?? FPS_DROP_SUSTAINED_WINDOWS
  const { minFps } = input

  if (minFps < threshold) {
    const lowStreak = input.state.lowStreak + 1
    // Borda exata: emite quando ATINGE o sustained, não depois.
    if (lowStreak === sustained) {
      return { type: 'emit', minFps, lowStreak }
    }
    return { type: 'silent', lowStreak }
  }

  return { type: 'silent', lowStreak: 0 }
}
